import { useState, useEffect, useRef } from "react";
import { faTimes, faPencil } from "@fortawesome/free-solid-svg-icons";
import { Chip } from "@mui/material";

import colors from "assets/styles/colors";
import Alert from "components/Common/Alert/Alert";
import ButtonSmall from "components/Common/ButtonSmall/ButtonSmall";
import Button from "components/Common/Button/Button";
import Tabs from "components/Common/Tabs/Tabs";
import * as Database from "utils/database.utils";
import { useSettings } from "context/Settings.context";

import CompanyModalContainer from "../CompanyModal.styles";
import InfoTab, { InfoTabRef } from "./InfoTab";
import HistoryTab from "./HistoryTab";

type Props = {
    company: any;
    setSelectedCompany: Function;
    reloadCompanies?: Function;
};

type CompanyData = {
    id: string;
    name: string;
    ticker: string;
    team: string;
    analyst_id: string | null;
    gef_focus_list: boolean;
    aef_focus_list: boolean;
    link_to_model: string | null;
    ipo_flag: boolean;
    cyclicality: string | null;
    isin: string | null;
    sedol: string | null;
    cusip: string | null;
    genim_gics_sector: string | null;
    gef_max_wgt_adj: number | null;
    packaged_software: boolean;
    med_tools: boolean;
    status: string | null;
    company_category_items: Array<{
        category_item_id: number;
        category_id: number;
        percentage: number;
    }>;
    company_links: Array<{
        id: number;
        link_type_id: number;
        url: string;
    }>;
};

export default function CompanyDetailsModal({
    company,
    setSelectedCompany,
    reloadCompanies,
}: Props) {
    const { setToast, setShowLoadingToast } = useSettings();
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [companyData, setCompanyData] = useState<CompanyData | null>(null);
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const [currentTab, setCurrentTab] = useState("INFO");
    const [isBbgFetching, setIsBbgFetching] = useState(false);
    const infoTabRef = useRef<InfoTabRef>(null);

    const fetchCompanyData = async () => {
        setLoading(true);
        try {
            const res = await Database.get(`api/company/${company.id}`);
            if (res.ok) {
                const data = await res.json();
                setCompanyData(data);
            } else {
                setToast({
                    show: true,
                    type: "error",
                    message: "Failed to load company details",
                });
            }
        } catch (err) {
            setToast({
                show: true,
                type: "error",
                message: "Failed to load company details",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanyData();
    }, [company.id]);

    const handleSave = async () => {
        if (!infoTabRef.current) return;

        const errors = infoTabRef.current.validate();
        if (Object.keys(errors).length > 0) return;

        setSaving(true);
        setShowLoadingToast({
            show: true,
            message: "UPDATING COMPANY",
        });

        try {
            const updatedData = await infoTabRef.current.save();

            const res = await Database.put(
                `api/company/${company.id}`,
                updatedData
            );

            if (res.ok) {
                setToast({
                    show: true,
                    type: "success",
                    message: "Company updated successfully",
                });
                setEditMode(false);
                fetchCompanyData();
                reloadCompanies?.();
            } else {
                const errorData = await res.json();
                if (errorData.error && typeof errorData.error === "object") {
                    const newErrors: Record<string, string> = {};
                    Object.entries(errorData.error).forEach(
                        ([field, messages]) => {
                            if (
                                Array.isArray(messages) &&
                                messages.length > 0
                            ) {
                                newErrors[field] = messages[0] as string;
                            }
                        }
                    );
                    infoTabRef.current?.setFieldErrors(newErrors);
                } else {
                    setToast({
                        show: true,
                        type: "error",
                        message: errorData.error || "Failed to update company",
                    });
                }
            }
        } catch (err) {
            setToast({
                show: true,
                type: "error",
                message: "Failed to update company",
            });
        } finally {
            setSaving(false);
            setShowLoadingToast({
                show: false,
                message: "",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditMode(false);
        fetchCompanyData();
    };

    const handleClose = () => {
        if (editMode) {
            setShowConfirmClose(true);
        } else {
            setSelectedCompany(null);
        }
    };

    return (
        <CompanyModalContainer>
            <div className={"modalInner"}>
                <div className={"header"}>
                    <div className={"topRow"}>
                        <div className={"title"}>
                            <h1>{company.name}</h1>
                            {companyData?.status && (
                                <Chip
                                    label={companyData.status.toUpperCase()}
                                    size="small"
                                    sx={{
                                        fontWeight: "bold",
                                        borderRadius: "4px",
                                        backgroundColor:
                                            companyData.status === "confirmed"
                                                ? `${colors.accentGreen}26`
                                                : `${colors.accentOrange}26`,
                                        color:
                                            companyData.status === "confirmed"
                                                ? colors.accentGreen
                                                : colors.accentOrange,
                                    }}
                                />
                            )}
                        </div>

                        <div className={"headerActions"}>
                            {!editMode && currentTab === "INFO" && (
                                <ButtonSmall
                                    text={"EDIT"}
                                    color={"primary"}
                                    icon={faPencil}
                                    click={() => setEditMode(true)}
                                />
                            )}
                            <ButtonSmall
                                text={"CLOSE"}
                                color={"third"}
                                icon={faTimes}
                                click={handleClose}
                            />
                        </div>
                    </div>
                </div>

                <Tabs
                    defaultTab={"INFO"}
                    setTab={setCurrentTab}
                    dontChangeUrl
                    tabs={[
                        {
                            title: "INFO",
                            value: "INFO",
                            content: (
                                <InfoTab
                                    ref={infoTabRef}
                                    companyData={companyData}
                                    loading={loading}
                                    editMode={editMode}
                                    onBbgFetchingChange={setIsBbgFetching}
                                />
                            ),
                        },
                        {
                            title: "HISTORY",
                            value: "HISTORY",
                            content: <HistoryTab companyId={company.id} />,
                            hide: editMode,
                        },
                        {
                            title: "EXTRACTIONS",
                            value: "EXTRACTIONS",
                            permissions: ["p_list_extractions"],
                            content: <div>Coming soon</div>,
                            hide: editMode,
                        },
                        {
                            title: "STOCK SPLITS",
                            value: "STOCK_SPLITS",
                            content: <div>Coming soon</div>,
                            hide: editMode,
                        },
                        {
                            title: "BQMQ",
                            value: "BQMQ",
                            permissions: ["p_bqmq_can_view_results"],
                            content: <div>Coming soon</div>,
                            hide: editMode,
                        },
                    ]}
                />

                {editMode && (
                    <div className={"actions"}>
                        <Button
                            text={"CANCEL"}
                            color={"third"}
                            click={handleCancelEdit}
                            disabled={saving}
                        />
                        <Button
                            text={saving ? "SAVING..." : "SAVE"}
                            color={"primary"}
                            click={handleSave}
                            disabled={saving || isBbgFetching}
                        />
                    </div>
                )}
            </div>

            {showConfirmClose && (
                <Alert>
                    <h2>ARE YOU SURE?</h2>
                    <p>Changes will be lost.</p>
                    <Button
                        text="YES, CLOSE"
                        color="accent"
                        click={() => setSelectedCompany(null)}
                    />
                    <Button
                        text="CANCEL"
                        color="flat"
                        click={() => setShowConfirmClose(false)}
                    />
                </Alert>
            )}
        </CompanyModalContainer>
    );
}
